/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { fetchGraphQl } from '@/quote-management/api';
import { REMOVE_NEGOTIABLE_QUOTE_TEMPLATE_ITEMS_MUTATION } from './graphql/removeNegotiableQuoteTemplateItems';
import { transformQuoteTemplate } from '@/quote-management/data/transforms';
import { state } from '@/quote-management/lib/state';
import { events } from '@adobe-commerce/event-bus';

export interface RemoveQuoteTemplateItemsParams {
  templateId: string;
  itemUids: string[];
}

export const removeQuoteTemplateItems = async (
  params: RemoveQuoteTemplateItemsParams
) => {
  if (!params.templateId) {
    throw new Error('Template ID is required');
  }

  if (!params.itemUids || params.itemUids.length === 0) {
    throw new Error('Item UIDs array is required and must not be empty');
  }

  if (!state.authenticated) {
    throw new Error('Unauthorized');
  }

  try {
    const results: any = await fetchGraphQl(
      REMOVE_NEGOTIABLE_QUOTE_TEMPLATE_ITEMS_MUTATION,
      {
        variables: {
          input: {
            template_id: params.templateId,
            item_uids: params.itemUids,
          },
        },
      }
    );

    if (!results?.data?.removeNegotiableQuoteTemplateItems) {
      throw new Error('No quote template data received');
    }

    const transformedData = transformQuoteTemplate(
      results.data.removeNegotiableQuoteTemplateItems
    );

    if (!transformedData) {
      throw new Error('Failed to transform quote template data');
    }

    events.emit('quote-management/quote-template-data', {
      quoteTemplate: transformedData,
      permissions: state.permissions,
    });

    return transformedData;
  } catch (error) {
    return Promise.reject(error);
  }
};
